import {
  ListType,
  ListsEditor,
  onKeyDown,
  withLists as withListsPluginCreator,
  withListsReact,
} from '@prezly/slate-lists'
import { SlateNode } from '@src/components/sco/RichMarkdownInput'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import { isHotkey } from 'is-hotkey'
import { Editor, Element, Node, NodeEntry, Range, Transforms } from 'slate'

const BULLET_TRIGGERS = ['-', '*', '•', '--']

export function withLists(editor, options = {}) {
  const editorWithLists = withListsReact(
    withListsPluginCreator({
      isConvertibleToListTextNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.PARAGRAPH)
      },
      isDefaultTextNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.PARAGRAPH)
      },
      isListNode(node: Node, type: ListType) {
        if (type) {
          return Element.isElementType(node, type)
        }
        return (
          Element.isElementType(node, EditorElementTypes.OL_LIST) ||
          Element.isElementType(node, EditorElementTypes.UL_LIST)
        )
      },
      isListItemNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.LIST_ITEM)
      },
      isListItemTextNode(node: Node) {
        return Element.isElementType(node, EditorElementTypes.LIST_ITEM_TEXT)
      },
      createDefaultTextNode(props = {}) {
        return { type: EditorElementTypes.PARAGRAPH, children: [{ text: '' }], ...props }
      },
      createListNode(type: ListType = ListType.ORDERED, props = {}) {
        const nodeType = type === ListType.ORDERED ? EditorElementTypes.OL_LIST : EditorElementTypes.UL_LIST
        return { type: nodeType, children: [{ text: '' }], ...props }
      },
      createListItemNode(props = {}) {
        return { type: EditorElementTypes.LIST_ITEM, children: [{ text: '' }], ...props }
      },
      createListItemTextNode(props = {}) {
        return {
          type: EditorElementTypes.LIST_ITEM_TEXT,
          children: [{ type: EditorElementTypes.PARAGRAPH, children: [{ text: '' }] }],
          ...props,
        }
      },
    })(editor)
  )

  const onKeyDownOriginal = editor.onKeyDown
  editorWithLists.onKeyDown = (editor, event) => {
    onKeyDown(editorWithLists, event)

    if (isHotkey('backspace', event.nativeEvent)) {
      const { selection } = editorWithLists

      if (selection && Range.isCollapsed(selection)) {
        const [emptyNode]: Generator<NodeEntry<Node & { children: { text?: string }[] }>> = Editor.nodes(
          editor,
          {
            match: node =>
              Element.isElementType(node, EditorElementTypes.LIST_ITEM_TEXT) &&
              (node as SlateNode).children[0].text == '',
            at: selection,
          }
        )

        if (emptyNode) {
        }
      }
    }

    if (isHotkey('space', event.nativeEvent)) {
      const { selection } = editorWithLists

      if (selection && Range.isCollapsed(selection)) {
        const [paragraphNode]: Generator<NodeEntry<Node & { children: { text?: string }[] }>> = Editor.nodes(
          editor,
          {
            match: node =>
              Element.isElementType(node, EditorElementTypes.PARAGRAPH) &&
              // @ts-ignore
              (BULLET_TRIGGERS.find(trigger => trigger == node.children[0].text) ||
                // @ts-ignore
                node.children[0].text?.match(/^[\d]+[.]/)),
            at: selection,
          }
        )

        if (paragraphNode) {
          event.preventDefault()

          if (BULLET_TRIGGERS.find(trigger => trigger == paragraphNode[0].children[0].text)) {
            Transforms.delete(editor, { distance: 1, unit: 'word', reverse: true })
            ListsEditor.wrapInList(editor, ListType.UNORDERED)
          }
          if (paragraphNode[0].children[0].text.match(/^[\d]+[.]/)) {
            Transforms.delete(editor, { distance: 1, unit: 'word', reverse: true })
            ListsEditor.wrapInList(editor, ListType.ORDERED)
          }
        }
      }
    }

    if (onKeyDownOriginal) {
      onKeyDownOriginal(editor, event)
    }
  }

  return editorWithLists
}

export const wrapListTextNodes = (nodes: any) => {
  return nodes.map((n: any) => {
    if (
      n.type === EditorElementTypes.LIST_ITEM &&
      n.children &&
      n.children[0]?.type !== EditorElementTypes.LIST_ITEM_TEXT
    ) {
      return {
        ...n,
        children: [
          {
            type: EditorElementTypes.LIST_ITEM_TEXT,
            children: n.children[0]?.children || [{ text: '' }],
          },
        ],
      }
    }
    if (n.children && n.children.length > 0) {
      return {
        ...n,
        children: wrapListTextNodes(n.children),
      }
    }
    return n
  })
}

export const unwrapListTextNodes = (nodes: any) => {
  return nodes.map((n: any) => {
    if (n.type === EditorElementTypes.LIST_ITEM_TEXT) {
      return {
        type: 'paragraph',
        children: n.children,
      }
    }
    if (n.children && n.children.length > 0) {
      return {
        ...n,
        children: unwrapListTextNodes(n.children),
      }
    }
    return n
  })
}
